
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { useAuth } from "@/contexts/auth";
import { UserCircle, Menu, Users, Inbox } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";

const Navbar = () => {
  const {
    user,
    signOut
  } = useAuth();
  const isMobile = useIsMobile();
  return <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="container flex h-16 items-center justify-between">
        <Link to="/" className="flex items-center">
          <img 
            src="/lovable-uploads/df781da3-4e85-4cba-bd1c-86be3b12b486.png" 
            alt="LabSwAPP Logo" 
            className="h-8 mr-2"
          />
        </Link>

        <nav className="hidden md:flex mx-6 flex-1 items-center space-x-4 lg:space-x-6">
          <Link to="/reagents" className="text-sm font-medium transition-colors hover:text-primary">
            Browse Reagents
          </Link>
          <Link to="/reagent-requests" className="text-sm font-medium transition-colors hover:text-primary">
            Requested Reagents
          </Link>
          {user && <>
              <Link to="/reagents/new" className="text-sm font-medium transition-colors hover:text-primary">
                Upload Reagent
              </Link>
              <Link to="/request-reagent" className="text-sm font-medium transition-colors hover:text-primary">
                Request Reagent
              </Link>
              <Link to="/my-reagents" className="text-sm font-medium transition-colors hover:text-primary">
                My Reagents
              </Link>
              <Link to="/messages" className="text-sm font-medium transition-colors hover:text-primary">
                Messages
              </Link>
              <Link to="/community" className="text-sm font-medium transition-colors hover:text-primary">
                Community
              </Link>
            </>}
        </nav>

        {isMobile ? <MobileMenu user={user} logout={signOut} className="ml-auto" /> : <DesktopMenu user={user} logout={signOut} />}
      </div>
    </header>;
};

const DesktopMenu = ({
  user,
  logout
}: {
  user: any;
  logout: () => void;
}) => <div className="flex items-center space-x-4">
    <Link to="/reagents" className="text-gray-600 hover:text-reagent-blue transition-colors">
      Browse Reagents
    </Link>
    <Link to="/reagent-requests" className="text-gray-600 hover:text-reagent-blue transition-colors">
      Requested Reagents
    </Link>
    <Link to="/help" className="text-gray-600 hover:text-reagent-blue transition-colors">
      Help
    </Link>
    
    {user ? <>
        <Link to="/reagents/new" className="text-gray-600 hover:text-reagent-blue transition-colors">
          Upload Reagent
        </Link>
        
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" className="ml-2">
              <UserCircle className="h-4 w-4 mr-1" />
              My Account
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuLabel>My Account</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem asChild>
              <Link to="/profile" className="w-full cursor-pointer">Profile</Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <Link to="/my-reagents" className="w-full cursor-pointer">My Reagents</Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <Link to="/request-reagent" className="w-full cursor-pointer">Request Reagent</Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <Link to="/messages" className="w-full cursor-pointer">Messages</Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <Link to="/community" className="w-full cursor-pointer">Community</Link>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={logout} className="text-red-500 cursor-pointer">
              Logout
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </> : <div className="flex space-x-2">
        <Button asChild variant="outline" size="sm">
          <Link to="/login">Login</Link>
        </Button>
        <Button asChild size="sm">
          <Link to="/register">Register</Link>
        </Button>
      </div>}
  </div>;

const MobileMenu = ({
  user,
  logout,
  className
}: {
  user: any;
  logout: () => void;
  className?: string;
}) => <DropdownMenu>
    <DropdownMenuTrigger asChild>
      <Button variant="outline" size="icon" className={className}>
        <Menu className="h-5 w-5" />
      </Button>
    </DropdownMenuTrigger>
    <DropdownMenuContent align="end" className="w-56">
      <DropdownMenuItem asChild>
        <Link to="/reagents" className="w-full cursor-pointer">Browse Reagents</Link>
      </DropdownMenuItem>
      <DropdownMenuItem asChild>
        <Link to="/reagent-requests" className="w-full cursor-pointer">Requested Reagents</Link>
      </DropdownMenuItem>
      <DropdownMenuItem asChild>
        <Link to="/help" className="w-full cursor-pointer">Help</Link>
      </DropdownMenuItem>
      
      {user ? <>
          <DropdownMenuItem asChild>
            <Link to="/reagents/new" className="w-full cursor-pointer">Upload Reagent</Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <Link to="/request-reagent" className="w-full cursor-pointer">Request Reagent</Link>
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuLabel>My Account</DropdownMenuLabel>
          <DropdownMenuItem asChild>
            <Link to="/profile" className="w-full cursor-pointer">Profile</Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <Link to="/my-reagents" className="w-full cursor-pointer">My Reagents</Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <Link to="/messages" className="w-full cursor-pointer">Messages</Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <Link to="/community" className="w-full cursor-pointer">Community</Link>
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={logout} className="text-red-500 cursor-pointer">
            Logout
          </DropdownMenuItem>
        </> : <>
          <DropdownMenuSeparator />
          <DropdownMenuItem asChild>
            <Link to="/login" className="w-full cursor-pointer">Login</Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <Link to="/register" className="w-full cursor-pointer">Register</Link>
          </DropdownMenuItem>
        </>}
    </DropdownMenuContent>
  </DropdownMenu>;

export default Navbar;
